import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../App';

// Define the ordered list of SWF sections
const SWF_SECTIONS = [
  { file: 'text1.swf', title_en: 'Introduction', title_ta: 'அறிமுகம்' },
  { file: 'text2.swf', title_en: 'Preamble', title_ta: 'முன்னுரை' },
  { file: 'sath-pooja.swf', title_en: 'Pooja - Part 1', title_ta: 'பூஜை - பகுதி 1' },
  { file: 'sath-pooja2.swf', title_en: 'Pooja - Part 2', title_ta: 'பூஜை - பகுதி 2' },
  { file: 'sath-pooja3.swf', title_en: 'Pooja - Part 3', title_ta: 'பூஜை - பகுதி 3' },
  { file: 'sath-pooja4.swf', title_en: 'Pooja - Part 4', title_ta: 'பூஜை - பகுதி 4' },
  { file: 'sath-pooja5.swf', title_en: 'Pooja - Part 5', title_ta: 'பூஜை - பகுதி 5' },
  { file: 'sath-pooja6.swf', title_en: 'Pooja - Part 6', title_ta: 'பூஜை - பகுதி 6' },
  { file: 'sath-pooja7.swf', title_en: 'Pooja - Part 7', title_ta: 'பூஜை - பகுதி 7' },
  { file: 'sath-pooja8.swf', title_en: 'Pooja - Part 8', title_ta: 'பூஜை - பகுதி 8' },
  { file: 'sath-pooja9.swf', title_en: 'Pooja - Part 9', title_ta: 'பூஜை - பகுதி 9' },
  { file: 'sath-pooja10.swf', title_en: 'Pooja - Part 10', title_ta: 'பூஜை - பகுதி 10' },
  { file: 'sath-pooja11.swf', title_en: 'Pooja - Part 11', title_ta: 'பூஜை - பகுதி 11' },
  { file: 'sath-pooja12.swf', title_en: 'Pooja - Part 12', title_ta: 'பூஜை - பகுதி 12' },
  { file: 'sath-pooja14.swf', title_en: 'Pooja - Part 13', title_ta: 'பூஜை - பகுதி 13' },
  { file: 'sath-pooja15.swf', title_en: 'Pooja - Part 14', title_ta: 'பூஜை - பகுதி 14' },
  { file: 'sath-pooja16.swf', title_en: 'Pooja - Part 15', title_ta: 'பூஜை - பகுதி 15' },
  { file: 'sath-pooja17.swf', title_en: 'Pooja - Part 16', title_ta: 'பூஜை - பகுதி 16' },
];

const SWF_BASE_PATH = '/swf/sathya-narayana-pooja/';

// Individual SWF Player Component
function SwfPlayer({ swfFile, width = 550, height = 400 }) {
  const containerRef = useRef(null);
  const playerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const loadPlayer = () => {
      if (cancelled || !containerRef.current) return;
      if (!window.RufflePlayer) {
        // Ruffle not loaded yet, retry
        setTimeout(loadPlayer, 500);
        return;
      }

      try {
        // Clean up previous player if any
        if (playerRef.current) {
          try { playerRef.current.remove(); } catch (e) { /* ignore */ }
          playerRef.current = null;
        }
        if (containerRef.current) {
          containerRef.current.innerHTML = '';
        }

        const ruffle = window.RufflePlayer.newest();
        const player = ruffle.createPlayer();
        player.style.width = '100%';
        player.style.height = '100%';

        if (containerRef.current && !cancelled) {
          containerRef.current.appendChild(player);
          playerRef.current = player;
          player.load({ url: SWF_BASE_PATH + swfFile, autoplay: 'on' });
        }
      } catch (err) {
        console.error('Ruffle player error:', err);
      }
    };

    loadPlayer();

    return () => {
      cancelled = true;
      if (playerRef.current) {
        try { playerRef.current.remove(); } catch (e) { /* ignore */ }
        playerRef.current = null;
      }
    };
  }, [swfFile]);

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        maxWidth: `${width}px`,
        height: `${height}px`,
        margin: '0 auto',
        borderRadius: '8px',
        overflow: 'hidden',
        background: '#fff',
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
      }}
    />
  );
}

export default function SathyaNarayanaPooja() {
  const { language } = useAuth();
  const [currentSection, setCurrentSection] = useState(0);
  const [viewMode, setViewMode] = useState('text'); // 'text', 'single', or 'all'
  const [fontSize, setFontSize] = useState(16); // Font size for text mode
  const sectionRef = useRef(null);
  const isTamil = language === 'ta';

  // Complete, Unabridged Text content for Prayogam & Katha Reader View
  const TEXT_CONTENT = {
    0: {
      title_ta: 'அறிமுகம் & விரத மகிமை',
      title_en: 'Introduction & Vratam Glory',
      content_ta: `ஸ்ரீ ஸத்யநாராயண விரத பூஜை என்பது ஸ்ரீமந் நாராயணனின் மிக உன்னதமான மற்றும் உடனடியாக பலனளிக்கும் விரத பூஜையாகும். இந்த விரதத்தை மேற்கொள்வதால் குடும்பத்தில் வறுமை நீங்கி, அமைதி, செல்வம், மங்களம், மற்றும் சகல காரிய சித்திகளும் உண்டாகும்.

ஸ்காந்த புராணத்தின் ரேவா கண்டத்தில் இந்த விரதத்தின் பெருமைகள் விரிவாகக் கூறப்பட்டுள்ளன. நாரத முனிவர் பூலோகத்து மக்களின் துன்பங்களைக் கண்டு வருந்தி, வைகுண்டம் சென்று பகவான் நாராயணனிடம் "சுலபமாக அனைவரும் செய்து பலன் பெறக்கூடிய விரதம் எது?" என்று வினவினார். பகவான் ஸ்ரீமந் நாராயணன் நாரதருக்கு உபதேசித்த உன்னதமான பூஜையே இந்த ஸ்ரீ ஸத்ய நாராயண விரத பூஜையாகும். ஏழை, பணக்காரர், ஆண்கள், பெண்கள் என்ற பாகுபாடின்றி எவரொருவர் பக்தியுடன் இந்நாளில் விரதமிருந்து பகவானை பூஜித்து கதையைக் கேட்கிறார்களோ, அவர்களின் பாபங்கள் அகன்று சகல பாக்கியங்களும் சித்திக்கும்.`,
      content_en: `Sri Sathya Narayana Vratha Pooja is one of the most sacred and highly auspicious rituals dedicated to Lord Sri Maha Vishnu (Sathya Narayana Swamy). Performing this Vratham brings immense peace, harmony, prosperity, health, and spiritual evolution to the entire household.

Described elaborately in the Reva Kanda of Skanda Puranam, Sage Narada once visited the Earth and was deeply moved by the suffering of human beings. He went to Vaikuntham and prayed to Lord Narayana to prescribe a simple yet all-powerful vrata that can be performed easily by anyone. Lord Narayana revealed the Sri Sathya Narayana Vratam. Whoever performs this sacred pooja with devotion, listens to the divine Katha, and partakes in the sacred Prasadam will be freed from all sins and blessed with abundant prosperity and liberation.`
    },
    1: {
      title_ta: 'முன்னுரை & ஆசமனம் (Preamble & Achamanam)',
      title_en: 'Preamble & Achamanam',
      content_ta: `1. ஆசமனம் (புனித நீர் அருந்துதல்):
(வலது உள்ளங்கையில் தீர்த்தம் எடுத்துக்கொண்டு அருந்த வேண்டும்)
- ஓம் அச்யுதாய நமஹ (முதல் முறை அருந்துதல்)
- ஓம் அநந்தாய நமஹ (இரண்டாம் முறை அருந்துதல்)
- ஓம் கோவிந்தாய நமஹ (மூன்றாம் முறை அருந்துதல்)

(கைகளை அலம்பி வலது கை கட்டை விரலால் உதடுகளைத் துடைக்கவும்)
- ஓம் கேசவாய நமஹ, ஓம் நாராயணாய நமஹ (கன்னங்களைத் தொடுதல்)
- ஓம் மாதவாய நமஹ, ஓம் கோவிந்தாய நமஹ (கண்களைத் தொடுதல்)
- ஓம் விஷ்ணவே நமஹ, ஓம் மதுசூதனாய நமஹ (மூக்கைத் தொடுதல்)
- ஓம் திரிவிக்ரமாய நமஹ, ஓம் வாமனாய நமஹ (செவிகளைத் தொடுதல்)
- ஓம் ஸ்ரீதராய நமஹ, ஓம் ஹ்ருஷீகேசாய நமஹ (தோள்களைத் தொடுதல்)
- ஓம் பத்மநாபாய நமஹ, ஓம் தாமோதராய நமஹ (நாபி மற்றும் சிரசைத் தொடுதல்)

2. கணபதி த்யானம்:
சுக்லாம்பரதரம் விஷ்ணும் சசிவர்ணம் சதுர்புஜம் |
ப்ரஸன்னவதனம் த்யாயேத் ஸர்வ விக்னோபசாந்தயே ||

3. பிராணாயாமம்:
(வலது நாசியைப் பிடித்துக்கொண்டு மனதினுள் ஜபித்தல்)
ஓம் பூஹு | ஓம் புவஹ | ஓம் ஸுவஹ | ஓம் மஹஹ | ஓம் ஜனஹ | ஓம் தபஹ | ஓம் ஸத்யம் |
ஓம் தத்ஸவிதுர்வரேண்யம் பர்கோ தேவஸ்ய தீமஹி | தியோ யோ நஃ ப்ரசோதயாத் ||
ஓம் ஆபோ ஜ்யோதி ரஸோऽம்ருதம் ப்ரஹ்ம பூர்புவஸ்ஸுவரோம் ||`,
      content_en: `1. Achamanam (Sacred Purification):
(Take a spoon of water in the right palm and sip thrice while chanting)
- Om Achyuthaya Namaha (First Sip)
- Om Ananthaya Namaha (Second Sip)
- Om Govindhaya Namaha (Third Sip)

(Wash hands and touch sacred points on the face and body)
- Om Keshavaya Namaha, Om Narayanaya Namaha (Touch cheeks)
- Om Madhavaya Namaha, Om Govindhaya Namaha (Touch eyes)
- Om Vishnave Namaha, Om Madhusudhanaya Namaha (Touch nostrils)
- Om Trivikramaya Namaha, Om Vamanaya Namaha (Touch ears)
- Om Shreedharaya Namaha, Om Hrishikeshaya Namaha (Touch shoulders)
- Om Padmanabhaya Namaha, Om Damodharaya Namaha (Touch navel and head)

2. Ganapathi Dhyanam:
Shuklaambaradharam Vishnum Shashivarnam Chathurbhujam |
Prasannavadhanam Dhyayeth Sarva Vighnopashanthaye ||

3. Pranayamam (Breath Control Mantra):
Om Bhoohu | Om Bhuvaha | Om Suvaha | Om Mahaha | Om Janaha | Om Tapaha | Om Satyam |
Om Tatsaviturvarenyam Bhargo Devasya Dheemahi | Dhiyo Yo Nah Prachodayath ||
Om Apo Jyoti Raso Amritam Brahma Bhoorbhuvassuvarom ||`
    },
    2: {
      title_ta: 'பூஜை - பகுதி 1: விக்னேஸ்வர பூஜை & சங்கல்பம்',
      title_en: 'Pooja Part 1: Vigneshwara Pooja & Sankalpam',
      content_ta: `1. மகா சங்கல்பம்:
ஹரிஃ ஓம் தத்ஸத் | ஸ்ரீ கோவிந்த கோவிந்த கோவிந்த | அஸ்ய ஸ்ரீபகவதோ மஹாபுருஷஸ்ய விஷ்ணோராஜ்ஞயா ப்ரவர்த்தமானஸ்ய அத்ய ப்ரஹ்மணஃ த்விதீய பரார்த்தே ஸ்வேதவராஹ கல்பே வைவஸ்வத மன்வந்தரே அஷ்டாவிம்சதிதமே கலியுகே ப்ரதம பாதே ஜம்பூத்வீபே பாரதவர்ஷே பரதகண்டே மேரோஃ தக்ஷிண திக் பாகே அஸ்மின் வர்த்தமானே வ்யாவஹாரிகே ப்ரபவாதி ஷஷ்டி ஸம்வத்ஸராணாம் மத்யே... (தற்போதைய தமிழ் வருடம், அயனம், ரிது, மாதம், பக்ஷம், திதி, வார, நக்ஷத்ரம் கூறவும்).

அஸ்மாகம் ஸஹகுடும்பானாம் க்ஷேம ஸ்தைர்ய வீர்ய விஜய ஆயுர் ஆரோக்கிய ஐஸ்வர்யாபிலவிருத்யர்த்தம், ஸமஸ்த பாபக்ஷயார்த்தம், தர்மார்த்த காம மோக்ஷ சதுர்வித பலபுருஷார்த்த சித்தையர்த்தம், ஸ்ரீ ஸத்ய நாராயண ஸ்வாமி தேவதா ப்ரீத்யர்த்தம் யதாசக்தி த்யானாாவாஹனாதி ஷோடசோபசார பூஜாம் கரிஷ்யே.

2. விக்னேஸ்வர பூஜை:
மஞ்சள் பிள்ளையார் பிடித்து வைத்து புஷ்பம் மற்றும் அக்ஷதை சாற்றி பூஜித்தல்:
ஓம் கணபதயே நமஹ - த்யாயாமி, ஆவாஹயாமி, ஆஸனம் சமர்ப்பயாமி, பாத்யம், அர்க்யம், ஆசமனீயம், ஸ்நானம், வஸ்திரம், சந்தனம், குங்குமம், புஷ்பம் சமர்ப்பயாமி.
ஓம் வக்ரதுண்டாய நமஹ, ஓம் ஏகதந்தாய நமஹ, ஓம் கஜகர்ணகாய நமஹ, ஓம் லம்போதராய நமஹ, ஓம் விகடாய நமஹ, ஓம் விக்னராஜாய நமஹ, ஓம் விநாயாகாய நமஹ.
(நைவேத்யம்: வெல்லம்/வாழைப்பழம் நிவேதனம் செய்து கற்பூர ஆரத்தி காட்டவும்).`,
      content_en: `1. Maha Sankalpam (Sacred Determination):
Harih Om Tatsat | Sri Govinda Govinda Govinda | On this auspicious day, in the sacred land of Bharat, I (along with my family) resolve to perform the divine Sri Sathya Narayana Swamy Pooja for the removal of all sins, for health, longevity, wealth, peace, success, and for attaining the fourfold purusharthas (Dharma, Artha, Kama, Moksha).

2. Vigneshwara Pooja (Lord Ganesha Worship):
Making a small conical idol of Lord Ganesha with turmeric paste (Manjal Pillayar):
Om Ganapathaye Namaha - Dhyayami, Avahayami, Asanam Samarpayami, Paadyam, Arghyam, Snanam, Vastram, Chandanam, Pushpam Samarpayami.
Chanting Ganesha Names: Om Vakratundaya Namaha, Om Ekadantaya Namaha, Om Gajakarnakaya Namaha, Om Lambodaraya Namaha, Om Vignarajaya Namaha, Om Vinayakaya Namaha.
(Offering Jaggery/Banana as Naivedyam and waving Camphor Aarthi).`
    },
    3: {
      title_ta: 'பூஜை - பகுதி 2: கலச பூஜை & வருண ஆவாஹனம்',
      title_en: 'Pooja Part 2: Kalasa Pooja & Varuna Avahanam',
      content_ta: `1. கலச ஸ்தாபனம் & பூஜித்தல்:
(செம்பு அல்லது பித்தளை கலசத்தில் தூய நீர் நிரப்பி, ஏலக்காய், லவங்கம், பச்சைக்கற்பூரம், மாவிலை, மற்றும் தேங்காய் வைத்து கலசத்தை அலங்கரிக்க வேண்டும்)

மந்திரம்:
கலசஸ்ய முகே விஷ்ணுஃ கண்டே ருத்ரஃ சமாச்ரிதஃ |
மூலே தத்ரோ ஸ்திதோ பிரம்மா மத்யே மாத்ருகணாஃ ஸ்ம்ருதாஃ ||
குக்ஷௌ து ஸாகராஃ ஸர்வே ஸப்தத்வீபா வஸுந்தரா |
ருக்வேதோऽத யஜுர்வேதஃ ஸாமவேதோ ஹ்யதர்வணஃ ||
அங்கைச்ச ஸஹிதாஃ ஸர்வே கலசாம்பு சமாச்ரிதாஃ |

புண்ணிய நதிகள் ஆவாஹனம்:
கங்கே ச யமுனே சைவா கோதாவரி சரஸ்வதி |
நர்மதே சிந்து காவேரி ஜலேऽஸ்மின் ஸன்னிதிம் குரு ||

ஓம் வர்ணாய நமஹ - கலசாம்போஜே வர்ணம் ஆவாஹயாமி, த்யாயாமி, பூஜயாமி!
(கலச தீர்த்தத்தில் விரல்களை வைத்து துளசி மற்றும் மலர்களால் பூஜித்து, அந்த தீர்த்தத்தை பூஜை திரவியங்கள் மற்றும் பக்தர்கள் மீது தெளிக்கவும்).`,
      content_en: `1. Kalasa Sthapanam & Varuna Avahanam:
(Filling a brass/copper vessel with pure water, adding cardamom, cloves, camphor, placing mango leaves and a coconut on top)

Mantra:
Kalasasya Mukhe Vishnu Kante Rudra Samashritah |
Mule Tatro Sthito Brahma Madhya Matriganah Smritah ||
Kukshau Tu Sagarah Sarve Saptadwipa Vasundhara |
Rigvedo Atha Yajurvedah Samavedo Hyatharvanah ||
Angaishcha Sahitah Sarve Kalasambu Samashritah ||

Invoking Sacred Rivers:
Gange Cha Yamune Chaiva Godavari Saraswati |
Narmade Sindhu Kaveri Jalesmin Sannidhim Kuru ||

Om Varunaya Namaha - Invoking Lord Varuna and the holy rivers into the Kalasam water. Sprinkling the sanctified water over the pooja materials, altar, and devotees.`
    },
    4: {
      title_ta: 'பூஜை - பகுதி 3: நவகிரக & பீட பூஜை',
      title_en: 'Pooja Part 3: Navagraha & Peetha Pooja',
      content_ta: `1. நவகிரக & அஷ்டதிக்பாலகர் ஸ்தாபனம்:
சூர்யாதி நவகிரக தேவதாப்பியோ நமஹ!
- ஓம் ஸூர்யாய நமஹ (சூரியன் - கிழக்கு)
- ஓம் சந்திராய நமஹ (சந்திரன் - தென்கிழக்கு)
- ஓம் அங்காரகாய நமஹ (செவ்வாய் - தெற்கு)
- ஓம் புதாய நமஹ (புதன் - வடகிழக்கு)
- ஓம் பிருஹஸ்பதயே நமஹ (குரு - வடக்கு)
- ஓம் சுக்ராய நமஹ (சுக்ரன் - கிழக்கு)
- ஓம் சனைச்சராய நமஹ (சனி - மேற்கு)
- ஓம் ராகவே நமஹ (ராகு - தென்மேற்கு)
- ஓம் கேதவே நமஹ (கேது - வடமேற்கு)

அஷ்டதிக்பாலகர்கள்:
இந்திரன், அக்னி, யமன், நிருருதி, வருணன், வாயு, குபேரன், ஈசானன் ஆகிய அஷ்டதிக்பாலகர்களை ஆவாஹனம் செய்து மலர் தூவுதல்.

2. பீட பூஜை:
ஆஸன பீடத்தின் நான்கு மூலைகளிலும் நடுவிலும் பூஜித்தல்:
- ஓம் ஆதாரசக்தயே நமஹ
- ஓம் கூர்மாய நமஹ
- ஓம் அனந்தாய நமஹ
- ஓம் ப்ருதிவ்யை நமஹ
- ஓம் ஆசாராய நமஹ, ஓம் தர்மாய நமஹ, ஓம் ஞாநாய நமஹ, ஓம் வைராக்யாய நமஹ, ஓம் ஐஸ்வர்யாய நமஹ.
பீடத்தின் மத்தியில் பிம்பம்/படத்தை வைத்து ஸ்ரீ ஸத்ய நாராயண ஸ்வாமியை ஆவாஹனம் செய்யவும்.`,
      content_en: `1. Navagraha & Dikpalaka Invocation:
Chanting mantras for the Nine Planets (Surya, Chandra, Angaraka, Budha, Brihaspati, Shukra, Shanaishchara, Rahu, Ketu) and the Guardians of Eight Directions (Ashtadikpalakas: Indra, Agni, Yama, Nirriti, Varuna, Vayu, Kubera, Ishana).

2. Altar (Peetha) Pooja:
Worshipping the base of the sacred altar:
- Om Adharashaktaye Namaha
- Om Kurmaya Namaha
- Om Ananthaya Namaha
- Om Prithivyai Namaha
- Om Dharmaya Namaha, Om Gnanaya Namaha, Om Vairagyaya Namaha, Om Aishwaryaya Namaha.
Placing the divine idol or framed picture of Lord Sathya Narayana at the center of the altar.`
    },
    5: {
      title_ta: 'பூஜை - பகுதி 4: த்யானம் & ஆவாஹனம்',
      title_en: 'Pooja Part 4: Dhyanam & Avahanam',
      content_ta: `1. ஸ்ரீ ஸத்ய நாராயண த்யான ஸ்லோகம்:
சாந்தாகாரம் புஜகசயனம் பத்மநாபம் சுரேசம்
விஸ்வாதாரம் கதனஸத்ருசம் மேகவர்ணம் சுபாங்கம் |
லக்ஷ்மீகாந்தம் கமலநயனம் யோகிபிர் த்யானகம்யம்
வந்தே விஷ்ணும் பவபயஹரம் ஸர்வலோகைகநாதம் ||

சங்கசக்ர தரம் தேவம் கிரீடமகுடோஜ்வலம் |
பீதாம்பரதரம் திவ்யம் ஸத்யநாராயணம் பஜே ||

2. ஆவாஹனம்:
ஓம் ஆவாஹயாமி தேவ ஈச விஷ்ணோ ஸத்யநாராயண |
அஸ்மின் பிம்பே/பிரதிமாயாம் சுப்ரதிஷ்டிதோ பவ ||

- ஓம் ஸ்ரீ ஸத்ய நாராயண ஸ்வாமினே நமஹ - த்யாயாமி! (தியானிக்கிறேன்)
- ஓம் ஸ்ரீ ஸத்ய நாராயண ஸ்வாமினே நமஹ - ஆவாஹயாமி! (ஆவாஹனம் செய்கிறேன்)
- ஓம் ஸ்ரீ ஸத்ய நாராயண ஸ்வாமினே நமஹ - அக்ஷதான் சமர்ப்பயாமி! (மலர்களும் அக்ஷதையும் சாற்றுகிறேன்)`,
      content_en: `1. Dhyana Slokam (Meditation on Lord Vishnu):
Shantakaram Bhujagashayanam Padmanabham Suresham
Vishvatharam Gaganasadrisham Meghavarnam Shubhangam |
Lakshmikantam Kamalanayanam Yogibhirdhyanagamyam
Vande Vishnum Bhavabhayaharam Sarvalokaikanatham ||

Shankha Chakra Dharam Devam Kireeta Makutojjvalam |
Peethambaradharam Divyam Sathyanarayanam Bhaje ||

2. Avahanam (Divine Invocation):
Om Avahayami Deva Eesha Vishno Sathyanarayana |
Asmin Bimba/Pratimayam Supratishtitho Bhava ||

- Om Sri Sathya Narayana Swamine Namaha - Dhyayami (Meditate on the Lord)
- Om Sri Sathya Narayana Swamine Namaha - Avahayami (Invoke the Divine Presence)
- Om Sri Sathya Narayana Swamine Namaha - Akshataan Samarpayami (Offer flowers and sacred rice)`
    },
    6: {
      title_ta: 'பூஜை - பகுதி 5: ஆஸனம், பாத்யம், அர்க்யம் & ஆசமனீயம்',
      title_en: 'Pooja Part 5: Upacharas 1 to 4',
      content_ta: `1. ஆஸனம் (அரியணை சாற்றுதல்):
ரத்னசிம்மாஸனம் திவ்யம் ஸ்வர்ணரத்ன விபூஷிதம் |
க்ருஹாண தேவ தேவேச ஸத்யநாராயண நமோऽஸ்து தே ||
- ஓம் ஸ்ரீ ஸத்ய நாராயணாய நமஹ - ரத்ன சிம்மாஸனம் சமர்ப்பயாமி!

2. பாத்யம் (திருவடிகள் விளக்குதல்):
நாராயண நமஸ்தேऽஸ்து நரகார்ணவ தாரக |
பாத்யம் க்ருஹாண தேவேச மம ஸௌக்யம் ப்ரயச்ச மே ||
- ஓம் ஸ்ரீ ஸத்ய நாராயணாய நமஹ - பாதேயோ பாத்யம் சமர்ப்பயாமி!

3. அர்க்யம் (கைகள் அலம்புதல்):
விஷ்ணவே வஸுதேவாய நமஸ்தே பிரஹ்மரூபிணே |
அர்க்யம் க்ருஹாண தேவேச பக்தாநாமபயப்ரத ||
- ஓம் ஸ்ரீ ஸத்ய நாராயணாய நமஹ - ஹஸ்தயோ அர்க்யம் சமர்ப்பயாமி!

4. ஆசமனீயம் (பருகு நீர் அளித்தல்):
ஸர்வலோகைக நாதாய நாதாய ஜகதாமபி |
ஆசமனீயம் க்ருஹாணேதம் பக்தாநாமபயப்ரத ||
- ஓம் ஸ்ரீ ஸத்ய நாராயணாய நமஹ - முகே ஆசமனீயம் சமர்ப்பயாமி!`,
      content_en: `1. Asanam (Offering Divine Throne):
Ratnasimhasanam Divyam Swarnaratna Vibhooshitam | Offering a jewel-studded golden throne to Lord Sathya Narayana.
- Om Sri Sathya Narayanaya Namaha - Ratna Simhasanam Samarpayami!

2. Paadyam (Washing Divine Feet):
Offering sacred water to wash the Holy Feet of the Lord.
- Om Sri Sathya Narayanaya Namaha - Paadeyo Paadyam Samarpayami!

3. Arghyam (Washing Divine Hands):
Offering sanctified water to wash the Lotus Hands of the Lord.
- Om Sri Sathya Narayanaya Namaha - Hastayo Arghyam Samarpayami!

4. Achamaniyam (Drinking Water):
Offering pure water for sipping and mouth-cleansing.
- Om Sri Sathya Narayanaya Namaha - Mukhe Achamaniyam Samarpayami!`
    },
    7: {
      title_ta: 'பூஜை - பகுதி 6: பஞ்சாம்ருத ஸ்நானம்',
      title_en: 'Pooja Part 6: Panchamrutha Bathing Ritual',
      content_ta: `பஞ்சாம்ருத அபிஷேகம்:
1. க்ஷீர ஸ்நானம் (பசுவின் பால்):
காாமதேனு ஸமுத்பூதம் ததி க்ஷீரமனுத்தமம் | அபிஷேகார்த்தமயிநீதம் க்ருஹாண பரமேஸ்வர ||
- ஓம் ஸ்ரீ ஸத்ய நாராயணாய நமஹ - க்ஷீர ஸ்நானம் சமர்ப்பயாமி!

2. ததி ஸ்நானம் (தயிர்):
சந்த்ரமண்டல ஸங்காசம் ஸர்வஸௌக்யகரம் பரம் | ததி ஸ்நானம் மேயாதத்தம் க்ருஹாண பரமேஸ்வர ||
- ஓம் ஸ்ரீ ஸத்ய நாராயணாய நமஹ - ததி ஸ்நானம் சமர்ப்பயாமி!

3. ஆஜ்ய ஸ்நானம் (பசுவின் நெய்):
ஆஜ்யம் ஸுரினாம் ஆஹாரம் ஆஜ்யம் யஞே ப்ரதிஷ்டிதம் | ஆஜ்யம் ஸ்நானாய தேவேச க்ருஹாண பரமேஸ்வர ||
- ஓம் ஸ்ரீ ஸத்ய நாராயணாய நமஹ - ஆஜ்ய ஸ்நானம் சமர்ப்பயாமி!

4. மது ஸ்நானம் (தூய தேன்):
ஸர்வௌஷதி ஸமுத்பன்னம் மதுரம் மது பாவனம | மது ஸ்நானாய தேவேச க்ருஹாண பரமேஸ்வர ||
- ஓம் ஸ்ரீ ஸத்ய நாராயணாய நமஹ - மது ஸ்நானம் சமர்ப்பயாமி!

5. சர்க்கரா ஸ்நானம் (சர்க்கரை/கற்கண்டு):
இக்ஷுரஸ ஸமுத்பன்னா சர்க்கரா பாவனி சுபா | ஸ்நானார்த்தம் தேவ தேவேச க்ருஹாண பரமேஸ்வர ||
- ஓம் ஸ்ரீ ஸத்ய நாராயணாய நமஹ - சர்க்கரா ஸ்நானம் சமர்ப்பயாமி!

6. சுத்தோதக ஸ்நானம் (புனித நீர்):
மந்தாகின்யாஸ்து யத்தேயம் ஸர்வபாபஹரம் சுபம் | சுத்தோதக ஸ்நானமிதம் க்ருஹாண பரமேஸ்வர ||
- ஓம் ஸ்ரீ ஸத்ய நாராயணாய நமஹ - சுத்தோதக ஸ்நானம் சமர்ப்பயாமி! (துண்டினால் துடைத்தல்)`,
      content_en: `Panchamrutha Abhishekam (Holy Bathing Ritual):
1. Ksheera Snanam (Pure Cow Milk Bathing):
- Om Sri Sathya Narayanaya Namaha - Ksheera Snanam Samarpayami!
2. Dadhi Snanam (Curd/Yogurt Bathing):
- Om Sri Sathya Narayanaya Namaha - Dadhi Snanam Samarpayami!
3. Aajya Snanam (Pure Cow Ghee Bathing):
- Om Sri Sathya Narayanaya Namaha - Aajya Snanam Samarpayami!
4. Madhu Snanam (Natural Pure Honey Bathing):
- Om Sri Sathya Narayanaya Namaha - Madhu Snanam Samarpayami!
5. Sharkara Snanam (Unrefined Cane Sugar Bathing):
- Om Sri Sathya Narayanaya Namaha - Sharkara Snanam Samarpayami!
6. Shuddhodaka Snanam (Consecrated Holy Water Bathing):
- Om Sri Sathya Narayanaya Namaha - Shuddhodaka Snanam Samarpayami! (Gently drying with a silk towel).`
    },
    8: {
      title_ta: 'பூஜை - பகுதி 7: வஸ்திரம், யஞோபவீதம் & சந்தனம்',
      title_en: 'Pooja Part 7: Sacred Attire & Ornaments',
      content_ta: `1. வஸ்திரம் (பீதாம்பரம் & பட்டு வஸ்திரங்கள்):
சீதவாத உஷ்ண ஸந்தாப த்ராணம் லஜ்ஜா நிவாரணம் |
வஸ்த்ரயுக்மம் மயா தத்தம் க்ருஹாண பரமேஸ்வர ||
- ஓம் ஸ்ரீ ஸத்ய நாராயணாய நமஹ - வஸ்த்ரம் சமர்ப்பயாமி!

2. யஞோபவீதம் (பூணூல் சமர்ப்பணம்):
ப்ரஹ்மவிஷ்ணுமஹேசானாம் நிச்சிதம் ப்ரஹ்மஸூத்ரகம் |
யஞோபவீதம் மயா தத்தம் க்ருஹாண பரமேஸ்வர ||
- ஓம் ஸ்ரீ ஸத்ய நாராயணாய நமஹ - யஞோபவீதம் சமர்ப்பயாமி!

3. சந்தனம் & குங்குமம் (திவ்ய சந்தனம் சாற்றுதல்):
ஸ்ரீகண்டம் சந்தனம் திவ்யம் கந்தாட்யம் ஸுமனோஹரம் |
விலேபனம் க்ருஹாணேதம் ஸத்யநாராயண நமோऽஸ்து தே ||
- ஓம் ஸ்ரீ ஸத்ய நாராயணாய நமஹ - கந்தன்/சந்தனம் சமர்ப்பயாமி!

4. அக்ஷதை (மங்கள அக்ஷதை):
அக்ஷதாஸ்ச ஸுரஸ்ரேஷ்ட குங்குமாக்தாஃ ஸுசோபனாஃ |
மயா நிவேதிதா பக்த்யா க்ருஹாண பரமேஸ்வர ||
- ஓம் ஸ்ரீ ஸத்ய நாராயணாய நமஹ - அக்ஷதான் சமர்ப்பயாமி!`,
      content_en: `1. Vastram (Sacred Yellow/Silk Clothes):
Offering fine yellow silk robes (Peethambaram) to Lord Sathya Narayana.
- Om Sri Sathya Narayanaya Namaha - Vastram Samarpayami!

2. Yagnopavitham (Sacred Thread):
Offering three-fold holy thread woven with spiritual energy.
- Om Sri Sathya Narayanaya Namaha - Yagnopavitham Samarpayami!

3. Chandanam & Kumkumam (Sandalwood Paste & Vermillion):
Offering fragrant Mysore sandalwood paste and auspicious kumkum.
- Om Sri Sathya Narayanaya Namaha - Chandanam Samarpayami!

4. Akshathai (Holy Rice Grains mixed with Turmeric):
Offering sacred whole rice grains symbolizing complete abundance and devotion.
- Om Sri Sathya Narayanaya Namaha - Akshathaan Samarpayami!`
    },
    9: {
      title_ta: 'பூஜை - பகுதி 8: அங்க பூஜை & புஷ்ப அர்ச்சனை',
      title_en: 'Pooja Part 8: Anga Pooja & Flower Offerings',
      content_ta: `1. அங்க பூஜை (உடல் உறுப்புகளை மலரால் பூஜித்தல்):
- ஓம் நாராயணாய நமஹ - பாதௌ பூஜயாமி (திருவடிகள்)
- ஓம் மாதவாய நமஹ - ஜானுனீ பூஜயாமி (முழங்கால்கள்)
- ஓம் விஷ்ணவே நமஹ - ஊரூ பூஜயாமி (தொடைப்பகுதி)
- ஓம் பத்மநாபாய நமஹ - நாபிம் பூஜயாமி (நாபி)
- ஓம் வாசுதேவாய நமஹ - ஹ்ருதயம் பூஜயாமி (மார்பு)
- ஓம் கோவிந்தாய நமஹ - கண்டம் பூஜயாமி (கழுத்து)
- ஓம் புருஷோத்தமாய நமஹ - முகம் பூஜயாமி (திருமுகம்)
- ஓம் ஸர்வேஸ்வராய நமஹ - நேத்ரே பூஜயாமி (திருவிழிகள்)
- ஓம் அனந்தாய நமஹ - சிரஸ் பூஜயாமி (சிரசு)
- ஓம் ஸ்ரீ ஸத்ய நாராயணாய நமஹ - ஸர்வாண்யங்கானி பூஜயாமி (திருமேனி முழுவதும்)

2. புஷ்ப சமர்ப்பணம்:
மால்யாதீனி ஸுகந்தீனி மாலத்யாம்பூஜயானீ ச |
மயாஹ்ருதானி புஷ்பாணி க்ருஹாண பரமேஸ்வர ||
(துளசி தளங்கள், தாமரை, மல்லிகை, ரோஜா மற்றும் நறுமண மலர்களால் பூஜித்தல்).`,
      content_en: `1. Anga Pooja (Worshipping Divine Limbs):
Offering flowers to each holy part of the Lord's idol:
- Om Narayanaya Namaha - Paadau Poojayami (Lotus Feet)
- Om Madhavaya Namaha - Jaanuni Poojayami (Knees)
- Om Vishnave Namaha - Uroo Poojayami (Thighs)
- Om Padmanabhaya Namaha - Naabhim Poojayami (Navel)
- Om Vasudevaya Namaha - Hridayam Poojayami (Chest)
- Om Govindaya Namaha - Kantham Poojayami (Throat)
- Om Purushottamaya Namaha - Mukham Poojayami (Face)
- Om Sarveshwaraya Namaha - Nethre Poojayami (Eyes)
- Om Ananthaya Namaha - Shiras Poojayami (Head)
- Om Sri Sathya Narayanaya Namaha - Sarvaanyangani Poojayami (Entire Divine Body)

2. Pushpam (Flower Offering):
Offering fragrant Tulasi leaves, Lotus, Jasmine, and Rose blossoms.`
    },
    10: {
      title_ta: 'பூஜை - பகுதி 9: அஷ்டோத்தர சத நாமாவளி (108 திருநாமங்கள்)',
      title_en: 'Pooja Part 9: Ashtothara Satha Namavali (108 Names)',
      content_ta: `(துளசி மற்றும் மலர்களால் 108 திருநாமங்களையும் ஓதி அர்ச்சனை செய்ய வேண்டும்)

1. ஓம் ஸத்யநாராயணாய நமஹ
2. ஓம் தீனபන්ධவே நமஹ
3. ஓம் ஜகந்நாதாய நமஹ
4. ஓம் லக்ஷ்மீபதயே நமஹ
5. ஓம் கருணாஸாகராய நமஹ
6. ஓம் முகுந்தாய நமஹ
7. ஓம் வாசுதேவாய நமஹ
8. ஓம் அனந்தாய நமஹ
9. ஓம் தாமோதராய நமஹ
10. ஓம் கோவிந்தாய நமஹ
11. ஓம் பத்மநாபாய நமஹ
12. ஓம் கபோர்தராய நமஹ
13. ஓம் கமலநேத்ராய நமஹ
14. ஓம் லோகநாதாய நமஹ
15. ஓம் ஸ்ரீதரயே நமஹ
16. ஓம் கேசவாய நமஹ
17. ஓம் மாதவாய நமஹ
18. ஓம் மதுசூதனாய நமஹ
19. ஓம் தைத்யாரயே நமஹ
20. ஓம் விஷ்ணவே நமஹ
... (108 திருநாமங்களையும் பக்தியுடன் கூறி துளசி சாற்றுதல்)`,
      content_en: `Ashtothara Satha Namavali (Chanting 108 Holy Names of Lord Sri Sathya Narayana):
(Chant each holy name while placing a sacred Tulasi leaf or flower at the Lord's feet)

1. Om Sathyanarayanaya Namaha
2. Om Deenabandhave Namaha
3. Om Jagannathaya Namaha
4. Om Lakshmipataye Namaha
5. Om Karunasagaraya Namaha
6. Om Mukundaya Namaha
7. Om Vasudevaya Namaha
8. Om Ananthaya Namaha
9. Om Damodharaya Namaha
10. Om Govindhaya Namaha
11. Om Padmanabhaya Namaha
12. Om Kamalanethraya Namaha
... (Chanting all 108 divine names with profound reverence).`
    },
    11: {
      title_ta: 'பூஜை - பகுதி 10: தூபம், தீபம் & நைவேத்யம்',
      title_en: 'Pooja Part 10: Dhoopam, Deepam & Mahanaivedyam',
      content_ta: `1. தூபம் (நறுமண தூபம்):
வனஸ்பதிரஸோத்பன்னோ கந்தாட்யோ கந்த உத்தமஃ |
ஆக்ரேயஃ ஸர்வதேவாநாம் தூபோऽயம் ப்ரதிகுஹ்யதாம் ||
- ஓம் ஸ்ரீ ஸத்ய நாராயணாய நமஹ - தூபம் ஆக்ராபயாமி!

2. தீபம் (பசு நெய் தீபம்):
ஸாஜ்யம்ச வர்த்திஸம்யுக்தம் வஹ்நினா யோஜிதம் மயா |
தீபம் க்ருஹாண தேவேச த்ரைலோக்ய திமிராபஹ ||
- ஓம் ஸ்ரீ ஸத்ய நாராயணாய நமஹ - தீபம் தர்சயாமி!

3. மகா நைவேத்யம் (சிறப்புப் பிரசாதம்):
(சம்பா ரவை/கோதுமை ரவை, சர்க்கரை/வெல்லம், பசு நெய், பால், வாழைப்பழம் கலந்த ஸ்ரீ ஸத்ய நாராயண பிரசாதம், பாயசம், வடை, பழங்கள்)
ஓம் பூர்புவஸ்ஸுவஃ | தத்ஸவிதுர்வரேண்யம் பர்கோ தேவஸ்ய தீமஹி | தியோ யோ நஃ ப்ரசோதயாத் ||
தேவஸவிதஃ ப்ரஸுவ | ஸத்யம் தவர்த்தேன பரிஷிஞ்சாமி | அம்ருதோபஸ்தரணமஸி |
- ஓம் ப்ரணாய ஸ்வாஹா, ஓம் அபாநாய ஸ்வாஹா, ஓம் வ்யாநாய ஸ்வாஹா, ஓம் உதாநாய ஸ்வாஹா, ஓம் ஸமாநாய ஸ்வாஹா, ஓம் ப்ரஹ்மணே ஸ்வாஹா!
- ஓம் ஸ்ரீ ஸத்ய நாராயணாய நமஹ - மகா நைவேத்யம் சமர்ப்பயாமி!

4. தாம்பூலம் (வெற்றிலை பாக்கு):
பூகிபல ஸமாயுக்தம் நாகவல்லீ தலைர்யுதம் |
கற்பூர சூர்ண ஸம்யுக்தம் தாம்பூலம் ப்ரதிகுஹ்யதாம் ||
- ஓம் ஸ்ரீ ஸத்ய நாராயணாய நமஹ - கற்பூர தாம்பூலம் சமர்ப்பயாமி!`,
      content_en: `1. Dhoopam (Sacred Natural Incense):
Offering divine herbal incense to purify the mind and ambiance.
- Om Sri Sathya Narayanaya Namaha - Dhoopam Aghrapayami!

2. Deepam (Pure Ghee Diya):
Offering illuminated ghee lamps to dispel darkness and ignorance.
- Om Sri Sathya Narayanaya Namaha - Deepam Darshayami!

3. Maha Naivedyam (Sacred Sathya Narayana Special Prasad):
Offering special Prasad cooked with Wheat Rava/Semolina, Sugar/Jaggery, Pure Cow Ghee, Milk, and ripe Bananas, along with Payasam, Fruits, and Coconut.
Chanting Gayatri Mantra & Prana Ahuti Mantras:
- Om Pranaya Swaha, Om Apanaya Swaha, Om Vyanaya Swaha, Om Udanaya Swaha, Om Samanaya Swaha, Om Brahmane Swaha!
- Om Sri Sathya Narayanaya Namaha - Maha Naivedyam Samarpayami!

4. Tamboolam (Betel Leaves & Areca Nut):
Offering fresh Betel leaves, betel nuts, and cardamom.
- Om Sri Sathya Narayanaya Namaha - Tamboolam Samarpayami!`
    },
    12: {
      title_ta: 'பூஜை - பகுதி 11: கற்பூர நீரஞ்சனம் & மங்கள ஆர்த்தி',
      title_en: 'Pooja Part 11: Camphor Aarthi & Mantrapushpam',
      content_ta: `1. கற்பூர நீரஞ்சனம் (மங்கள கற்பூர ஆரத்தி):
ராஜாதி ராஜாய ப்ரஸஹ்ய ஸாஹிநே | நமோ வயம் வைச்ரவணாய குர்வ்மஹே ||
ஸ மே காமான் காமகாமாய மஹ்யம் | காமேஸ்வரோ வைச்ரவணோ ததாது ||
குபேராய வைச்ரவணாய | மஹாராஜாய நமஹ ||

ஓம் நாராயணாய வித்மஹே வாஸுதேவாய தீமஹி | தன்னோ விஷ்ணுஃ ப்ரசோதயாத் ||

2. மங்கள ஆரத்தி தர்சனம் & மந்திரபுஷ்பம்:
கற்பூர தீப தர்சனம் சமர்ப்பயாமி. கைகளினால் ஆரத்தி உஷ்ணத்தை கண்களில் ஒற்றிக் கொள்ளுதல்.
மந்திரபுஷ்பம்:
யதா புஷ்பம் ச பலதம் யதா பாக்யம் ச ததா பலம் |
மந்திரபுஷ்பம் க்ருஹாணேதம் ஸத்யநாராயண நமோऽஸ்து தே ||`,
      content_en: `1. Karpura Neerajanam (Camphor Aarthi):
Waving bright Camphor Light while chanting Rajadhirajaya Prasahya Sahine... Vishnu Gayatri Mantra:
Om Narayanaya Vidmahe Vasudevaya Dheemahi | Tanno Vishnuh Prachodayath ||

2. Mantrapushpam & Aarthi Reception:
Offering holy flowers of devotion (Mantrapushpam) and receiving the divine blessings of the Karpura Aarthi light onto the eyes and forehead.`
    },
    13: {
      title_ta: 'விரத கதை - அத்யாயம் 1 (Chapter 1: Origin & Glory of the Vratam)',
      title_en: 'Chapter 1: Origin & Glory of the Vratam',
      content_ta: `முதல் அத்யாயம்:
நைமிசாரண்யம் என்ற புண்ணிய க்ஷேத்திரத்தில் சௌனகர் முதலிய எண்பத்து நான்காயிரம் ரிஷிகள் ஒன்று கூடி, சர்வ புராண சாஸ்திரங்களையும் அறிந்த சூத பௌராணிகரிடம் பணிவுடன் வினவினர்:
"ஹே முனிஸ்ரேஷ்டரே! கலியுகத்தில் மனிதர்கள் அல்ப ஆயுள், வறுமை, நோய் மற்றும் மனக்கவலைகளால் பீடிக்கப்பட்டு வாழ்கின்றனர். அவர்கள் எவ்விதமான எளிய விரதத்தைச் செய்தால் தங்களின் அனைத்து பாபங்களும் நீங்கி, இஹலோகத்தில் சகல செல்வங்களையும், பரலோகத்தில் முக்தியையும் அடைவார்கள்? அதனை எங்களுக்குக் கருணையுடன் அருளிச்செய்ய வேண்டும்."

சூத பௌராணிகர் பெரு மகிழ்ச்சியுடன் கூறினார்:
"ரிஷிகளே! முற்காலத்தில் பிரம்ம புத்திரரான நாரத முனிவர் பூலோகத்திற்கு எழுந்தருளிய போது, மனிதர்கள் தங்களின் பூர்வ கர்மாவினால் நோய்களாலும் வறுமையினாலும் படும் வேதனைகளைக் கண்டு மிகுந்த இரக்கம் கொண்டார். அவர் உடனடியாக ஸ்ரீவைகுண்டம் சென்று சதுர்புஜங்களுடன், சங்கு சக்கர கதாதரராய் விளங்கும் ஸ்ரீமந் நாராயணனைத் துதித்தார்.

பகவான் நாராயணன் பிரசன்னமாகி, 'நாரதா! உலக நன்மையைக் கருதி நீ கேட்ட கேள்வி மிகவும் போற்றத்தக்கது. பூலோகத்தில் மனிதர்கள் தங்களின் துயரங்களிலிருந்து விடுபட 'ஸ்ரீ ஸத்ய நாராயண விரதம்' என்ற உன்னதமான பூஜையை இயற்ற வேண்டும். எந்த ஒரு மனிதன் பௌர்ணமி அல்லது சங்கராந்தி தினத்தில், மனத்தூய்மையுடன் பிராமணர்களுடனும், பந்துமித்திரர்களுடனும் சேர்ந்து இந்த பூஜையைச் செய்து, ரவை, சர்க்கரை, நெய், பால், வாழைப்பழம் கலந்த பிரசாதத்தைப் படைத்து, கதையைக் கேட்டு பிரசாதம் உட்கொள்கிறானோ, அவனுக்கு வறுமை நீங்கி, சகல தனதானியங்களும், புத்திர பாக்கியமும் சித்திக்கும். முடிவில் மோக்ஷமும் கிடைக்கும்' என்று அருளிச்செய்தார்."`,
      content_en: `Chapter 1: The Divine Origin of Sri Sathya Narayana Vratam
In the sacred forest of Naimisharanya, Sage Shaunaka and 84,000 rishis assembled and humbly asked Suta Puranika: "O Holy Seer! In Kali Yuga, humans suffer from short lifespan, poverty, diseases, and mental afflictions. Please tell us a simple yet supreme Vratam that can instantly liberate mankind from suffering and bestow both earthly prosperity and ultimate liberation (Moksha)."

Suta Puranika joyfully replied: "O Seers! Once, Sage Narada traveled across the Earth and was moved to tears seeing the immense suffering of mortals due to past karmas. Narada immediately ascended to Sri Vaikuntham and prayed to Lord Shreeman Narayana. The Lord appeared with His four arms holding the Conch, Discus, Mace, and Lotus.

Lord Vishnu revealed: 'O Narada! Out of compassion for humanity, I reveal the most sacred ritual—Sri Sathya Narayana Vratam. On any Full Moon (Pournami) or Sankranti day, if a devotee gathers with family and friends, performs this pooja with utmost devotion, offers special Prasad made of Wheat Rava, Sugar, Ghee, Milk, and Bananas, and listens to this divine Katha with faith, all their sins will be erased, poverty will turn into wealth, childless couples will be blessed with virtuous children, and finally they will attain Vaikuntha Moksham.'`
    },
    14: {
      title_ta: 'விரத கதை - அத்யாயம் 2 (Chapter 2: Story of Sadananda & Woodcutter)',
      title_en: 'Chapter 2: Story of Sadananda & Woodcutter',
      content_ta: `இரண்டாம் அத்யாயம்:
சூதர் கதையைத் தொடர்ந்தார்:
"ரிஷிகளே! காசி நகரில் சதானந்தர் என்ற பரம ஏழை பிராமணர் ஒருவர் வாழ்ந்து வந்தார். அவர் பசியினாலும் வறுமையினாலும் வாடி, தினமும் பிச்சை எடுத்து ஜீவனம் செய்து வந்தார். பக்தியில் தலைசிறந்த அவரைச் சோதிக்கவும், அவருக்கு அருள எண்ணிய பகவான் நாராயணர், ஒரு முதிய பிராமணர் வடிவில் அவர்முன் தோன்றி, 'பிராமணரே! ஏன் இப்படி வறுமையில் உழல்கிறாய்?' என்று கேட்டார்.

சதானந்தர் கண்ணீருடன் தன் வறுமையைக் கூறினார். முதியவர் வடிவில் வந்த பகவான், 'அன்பரே! ஸ்ரீ ஸத்ய நாராயண விரதம் என்ற உன்னதமான பூஜை உள்ளது. அதை நீ சிரத்தையுடன் செய்வாயாக. உன் வறுமை அகலும்' என்று பூஜை முறைகளைக் கூறி மறைந்தார்.

சதானந்தர் மறுநாளே பிச்சை எடுத்த தனத்தைக் கொண்டு மிக எளிமையாக ஸத்யநாராயண பூஜையைச் செய்தார். பகவானின் அருளால் அவரது வறுமை முற்றிலும் நீங்கி, அவர் மகா தனவந்தராக மாறினார்.

அவர் தினமும் பூஜையைச் செய்து வரும் நாளில், காட்டில் விறகு வெட்டி ஜீவனம் செய்யும் பரம ஏழை விறகுவெட்டி ஒருவன் தாகத்துடன் சதானந்தரின் வீட்டிற்கு வந்தான். அங்கு நடக்கும் பூஜையையும், பிராமணரின் ஐஸ்வர்யத்தையும் கண்டு வியந்து, 'சுவாமி! இது என்ன பூஜை?' எனக் கேட்டான். சதானந்தர் விரத மகிமையைக் கூறினார்.

அந்த விறகுவெட்டி தன் மனதிற்குள், 'இன்று விறகு விற்று வரும் பணத்தைக் கொண்டு நானும் இந்த பூஜையைச் செய்வேன்' என்று நேர்ந்துகொண்டான். அன்று விறகு சந்தைக்குச் சென்றபோது, வழக்கத்தை விட இரட்டிப்பு விலைக்கு விறகுகள் விற்றன! அளவிலா மகிழ்ச்சியடைந்த விறகுவெட்டி, வாழைப்பழம், சர்க்கரை, பால், நெய் வாங்கி வந்து தன் குடும்பத்துடன் விரதத்தைச் செய்து கதையைக் கேட்டான். பகவானின் அருளால் அவனும் மாபெரும் தனவந்தனாகி, இறுதியில் வைகுண்டப் பதவியை அடைந்தான்."`,
      content_en: `Chapter 2: The Grace upon Brahmin Sadananda and the Poor Woodcutter
Suta Puranika continued: "In the holy city of Kashi, there lived an intensely poor Brahmin named Sadananda who begged daily for survival. Taking pity on him, Lord Narayana disguised Himself as an elderly Brahmin, met Sadananda, and taught him the sacred Sathya Narayana Vratam.

Sadananda immediately collected materials from his daily alms and performed the pooja with deep faith. By the grace of Lord Narayana, his poverty vanished instantly, and he became immensely wealthy.

One day, while Sadananda was performing the pooja, a poor woodcutter arrived carrying a heavy bundle of firewood, parched with thirst. Astounded by the divine aura and prosperity of the Brahmin's house, the woodcutter inquired about the ritual. Sadananda explained the glory of Lord Sathya Narayana.

The woodcutter mentally vowed: 'Whatever money I earn by selling firewood today, I will use it to perform this Vratam.' Miraculously, that day he sold his firewood for double the normal price! Overjoyed, he bought milk, ghee, rava, sugar, and fruits, performed the pooja with his family, and partook in the Prasad. By Lord Narayana's grace, the woodcutter became rich, lived a happy life with children, and ultimately attained Vaikuntha."`
    },
    15: {
      title_ta: 'விரத கதை - அத்யாயம் 3 (Chapter 3: King Ulkamukha & Merchant Sadhu)',
      title_en: 'Chapter 3: King Ulkamukha & Merchant Sadhu',
      content_ta: `மூன்றாம் அத்யாயம்:
சூதர் கூறினார்:
"முற்காலத்தில் உல்காமுகன் என்ற நீதிமானான அரசன் தன் மனைவியுடன் நதிக்கரையில் ஸத்யநாராயண விரதத்தைச் செய்துகொண்டிருந்தான். அப்போது சாது என்ற செல்வந்த வைசிய வியாபாரி தன வணிகக் கப்பல்களுடன் கரை ஒதுங்கினான். அரசனின் பூஜையைக் கண்டு வணங்கி, 'அரசே! தாங்கள் செய்யும் இந்த பூஜையின் பலன் என்ன?' எனக் கேட்டான். அரசன், 'இது சந்தான பாக்கியத்தையும், சகல செல்வங்களையும் தரும் ஸத்யநாராயண விரதம்' என்றான்.

குழந்தை பாக்கியம் இல்லாத சாது வியாபாரி, 'பகவானே! எனக்குக் குழந்தை பிறந்தால் நான் இந்த பூஜையைச் செய்கிறேன்' என்று வேண்டிக் கொண்டான். சில காலத்தில் அவனது மனைவி லீலாவதி கர்ப்பமுற்று, ஒரு அழகிய பெண் குழந்தையைப் பெற்றெடுத்தாள். அக்குழந்தைக்கு 'கலாபதி' எனப் பெயரிட்டனர். ஆனால் சாது வியாபாரி, 'மகளுக்குக் திருமணம் நடக்கும் போது பூஜை செய்கிறேன்' என்று விரதத்தைத் தள்ளிப்போட்டான்.

கலாபதி பருவமடைந்ததும், காஞ்சன நகரைச் சேர்ந்த ஒரு நல்ல வணிக வாலிபனுக்கு அவளை மணம் முடித்துக் கொடுத்தான். ஆனால் அப்போதும் சாது வியாபாரி அகந்தையினாலும் பேராசையினாலும் விரதத்தைச் செய்ய மறந்தான்!

பகவான் நாராயணன் சாதுவின் மறதியைக் கண்டு சினமுற்றார். சாதுவும் அவனது மருமகனும் ரத்னபுரம் என்ற நகருக்கு வியாபாரத்திற்காகச் சென்றனர். அன்று இரவு ரத்னபுர அரச அரண்மனையில் திருடிய திருடர்கள், காவலர்கள் துரத்துவதைக் கண்டு, திருடிய நவரத்தின மூட்டைகளைச் சாது வியாபாரி தங்கியிருந்த கூடாரத்தில் போட்டுவிட்டுத் தப்பினர்.

காவலர்கள் சாதுவையும் அவனது மருமகனையும் திருடர்கள் எனக் கருதி கைது செய்து, அரசனிடம் ஒப்படைத்தனர். அரசன் சந்திரசேனன் அவர்களின் அனைத்துச் செல்வங்களையும் பறிமுதல் செய்து, இருவரையும் இருண்ட சிறையில் அடைத்தான்! ஊரில் சாதுவின் வீட்டிலும் திருடர்கள் புகுந்து அனைத்துச் செல்வங்களையும் கொள்ளையடித்தனர். லீலாவதியும் கலாபதியும் உடுக்க உடையும் உணவும் இன்றித் தெருவில் பிச்சை எடுக்கும் நிலைக்குத் தள்ளப்பட்டனர்!"`,
      content_en: `Chapter 3: The Story of Merchant Sadhu and the Consequences of Forgotten Vows
Suta Puranika narrated: "Once, a righteous King named Ulkamukha was performing Sathya Narayana Vratam on a riverbank. A wealthy merchant named Sadhu anchored his trading ships nearby, approached the King, and asked about the pooja. Learning that it bestows offspring and prosperity, the childless merchant vowed: 'Lord, if I am blessed with a child, I shall perform this Vratam.'

Soon, his wife Lilavati gave birth to a beautiful baby girl named Kalavathi. However, out of procrastination, Sadhu postponed the pooja saying, 'I will perform it during my daughter's wedding.' Years passed, Kalavathi grew up and was married to a noble young merchant from Kanchana Nagar. Yet, Sadhu failed to perform the promised Vratam due to greed and arrogance!

Lord Narayana decided to teach him a lesson. Sadhu and his son-in-law traveled to the kingdom of Ratnapura for trade. That night, real thieves stole royal jewels from the palace and, while fleeing from guards, dropped the stolen treasure inside Sadhu's tent.

The royal guards arrested Sadhu and his son-in-law as thieves! King Chandrasena confiscated all their wealth and threw them into a dark dungeon without trial. Back home, robbers broke into Sadhu's house, looted everything, leaving Lilavati and Kalavathi in utter poverty, forced to beg for food!"`
    },
    16: {
      title_ta: 'விரத கதை - அத்யாயம் 4 (Chapter 4: Redemption of Kalavathi & Dungeon Release)',
      title_en: 'Chapter 4: Redemption of Kalavathi & Dungeon Release',
      content_ta: `நான்காம் அத்யாயம்:
சூதர் கூறினார்:
"ஒருநாள் பசியினால் வாடிய கலாபதி உணவு தேடி அலைந்தபோது, ஒரு பிராமணர் வீட்டில் ஸத்யநாராயண விரத பூஜை நடப்பதைக் கண்டாள். அங்கு கதையைக் கேட்டு பிரசாதத்தைச் சாப்பிட்டு இரவு வீட்டிற்குத் திரும்பினாள். தாய் லீலாவதி, 'மகளே! ஏன் இவ்வளவு தாமதம்?' எனக் கேட்டாள்.

கலாபதி, 'அம்மா! ஒரு பிராமணர் வீட்டில் ஸத்யநாராயண பூஜை நடந்தது. அதைக் கண்டு பிரசாதம் வாங்கி வந்தேன்' என்றாள். லீலாவதி தன் கணவன் பகவானுக்குச் செய்த வாக்கையும், விரதத்தை மறந்த தவற்றையும் நினைத்து விம்மியழுதாள். தாய்-மகள் இருவரும் உடனடியாக அக்கம் பக்கத்தினரிடம் கடன் வாங்கி, பக்தியுடன் பகவான் ஸத்யநாராயணனிடம் மன்னிப்புக் கேட்டு பூஜையைச் செய்தனர்.

பகவான் நாராயணன் அவர்களின் பூஜையால் மகிழ்ந்தார். அன்று இரவே ரத்னபுர அரசன் கனவில் தோன்றி, 'அரசனே! சிறையில் அடைக்கப்பட்டுள்ள இரு வணிகர்களும் நிரபராதிகள். அவர்களை உடனடியாக விடுதலை செய்து, அவர்களிடம் பறிமுதல் செய்த செல்வத்தை இரட்டிப்பாகத் திருப்பிக் கொடு. இல்லையெனில் உன் ராஜ்யமும் குலமும் அழிந்துவிடும்!' என்று எச்சரித்தார்.

அரசன் திடுக்கிட்டு விழித்து, விடியற்காலையில் சிறைக்கதவைத் திறந்து சாதுவையும் மருமகனையும் விடுவித்து, அரச உடைகள் அணிவித்து, இரட்டிப்புச் செல்வத்தையும் அளித்து வழியனுப்பினான்.

வணிகர்கள் கப்பலில் திரும்பும்போது, பகவான் ஒரு துறவி வடிவில் வந்து, 'கப்பலில் என்ன இருக்கிறது?' எனக் கேட்டார். சாது வியாபாரி அகந்தையுடன், 'துறவியே! கப்பலில் செடி கொடிகளும் இலைகளும்தான் இருக்கின்றன. உமக்கு ஏன் இந்த வீண் கேள்வி?' எனக் கேலி செய்தான். துறவி, 'அப்படியே ஆகட்டும்!' எனக்கூறிச் சென்றார்.

அடுத்த கணமே கப்பல் எடையில்லாமல் தண்ணீரில் மிதந்தது! சாது உள்ளே சென்று பார்க்க, தங்கம் வைரம் அனைத்தும் இலை தழைகளாக மாறியிருந்தன! சாது மயங்கி விழுந்தான். மருமகன், 'மாமா! அத்துறவி சாதாரணமானவர் அல்ல. பகவான் ஸத்யநாராயணனே அவர்!' என்றான். சாது துறவியின் திருவடிகளில் விழுந்து கதறி அழுது மன்னிப்புக் கேட்டான். பகவான் அருள்கூர்ந்து செல்வத்தை மீண்டும் அளித்தார்.

ஊரை அடைந்ததும் சாது வீட்டிற்குத் தூது அனுப்பினான். கலாபதி தன் தாயுடன் ஸத்யநாராயண பூஜையைச் செய்துகொண்டிருந்தாள். செய்தி கேட்ட மகிழ்ச்சியில் கலாபதி பிரசாதத்தைச் சாப்பிடாமல் அவசரமாகக் கணவனைக் காண ஓடினாள்! பிரசாதத்தை அவமதித்ததால், மருமகனின் கப்பலும் அவனும் நீரில் மூழ்கினர்!

கலாபதி கதறியழுது உயிரை விடத் துணிந்தாள். அப்போது அசரீரி ஒலித்தது: 'கலாபதி! நீ என் பிரசாதத்தை உண்ணாமல் அவமதிக்கப்பட்டு வந்தாய். வீட்டிற்குச் சென்று பிரசாதத்தைச் சாப்பிட்டுவிட்டு வா!' கலாபதி ஓடிச்சென்று பிரசாதத்தை உண்டு வந்ததும், கப்பலும் கணவனும் கரையேறினர்! அதன்பின் சாது குடும்பத்தினர் ஆயுள் முழுவதும் மாதம்தோறும் ஸத்யநாராயண விரதத்தைச் செய்து, சகல பாக்கியங்களும் பெற்று இறுதியில் மோக்ஷம் அடைந்தனர்."`,
      content_en: `Chapter 4: The Redemption of Kalavathi and Deliverance from Suffering
Suta Puranika continued: "One day, while wandering in search of food, young Kalavathi saw a Sathya Narayana Pooja being performed at a Brahmin's house. She listened to the Katha, ate the sacred Prasad, and returned home late. When her mother Lilavati asked about the delay, Kalavathi explained the pooja. Lilavati wept, realizing that their suffering was due to forgetting their vow to Lord Narayana.

Lilavati and Kalavathi immediately borrowed pooja materials and performed the Sathya Narayana Vratam with intense devotion, begging for forgiveness. Lord Vishnu was pleased. That very night, He appeared in the dream of King Chandrasena of Ratnapura and warned: 'O King! The two merchants in your dungeon are innocent. Release them immediately and return double their wealth, or your entire kingdom will perish!'

The King awoke in awe, released Sadhu and his son-in-law at dawn, dressed them in royal attire, returned twice their confiscated treasure, and bade them farewell.

While sailing home, Lord Narayana tested Sadhu once more, approaching his ship disguised as an ascetic (Sanyasi). The Sanyasi asked, 'O Merchant, what is inside your ship?' Sadhu arrogantly lied, 'O Monk, why do you ask? My ship contains only dry leaves and weeds!' The Sanyasi replied, 'So be it!' and walked away.

Instantly, the heavy ship turned feather-light! Sadhu looked inside and was horrified to find all his gold and jewels turned into dry leaves! He collapsed in tears. His son-in-law said, 'Uncle, that Sanyasi was Lord Sathya Narayana Himself! Let us seek His mercy.' Sadhu ran and fell at the Sanyasi's feet, weeping and repenting. Lord Vishnu restored his immense wealth.

Upon reaching their hometown, Sadhu sent word to his house. Kalavathi was performing Sathya Narayana Pooja with her mother. Hearing that her husband had arrived, Kalavathi rushed to the shore without eating the sacred Prasad! Because she disrespected the Prasad, her husband's ship sank into the ocean along with him!

Kalavathi wept bitterly and prepared to end her life. Suddenly, a divine voice (Asareeri) echoed from the sky: 'O Kalavathi! You disrespected My Prasad by leaving without consuming it. Go back, eat the Prasad with devotion, and return!' Kalavathi ran home, ate the Prasad, and rushed back. Miraculously, the ship surfaced and her husband emerged unharmed! From that day, Sadhu's family performed the Vratam every Pournami, lived in divine happiness, and ascended to Vaikuntha."`
    },
    17: {
      title_ta: 'விரத கதை - அத்யாயம் 5 & மங்களம் (Chapter 5: King Tungadhwaja & Mangalam)',
      title_en: 'Chapter 5: King Tungadhwaja & Mangalam',
      content_ta: `ஐந்தாம் அத்யாயம் & மங்கள ஆரத்தி:
சூதர் கதையை நிறைவு செய்தார்:
"ரிஷிகளே! துங்கத்வஜன் என்ற அரசன் காட்டில் வேட்டையாடி விட்டுத் திரும்பும்போது, ஆடு மேய்க்கும் இடையர்கள் (கோபாலகர்கள்) ஒரு ஆலமரத்தடியில் ஒன்று கூடி, மிகுந்த பக்தியுடன் ஸ்ரீ ஸத்யநாராயண பூஜையைச் செய்துகொண்டிருப்பதைக் கண்டான்.

அரசன் தன் ராஜ அகந்தையினால் அங்கிருந்த இடையர்களைத் தாழ்வாக நினைத்து, அவர்களைச் சென்று வணங்காமல் தள்ளி அமர்ந்தான். பூஜையை முடித்த இடையர்கள் அன்புடன் அரசனிடம் வந்து ஸத்யநாராயண பிரசாதத்தை அளித்தனர். ஆனால் அரசன் அகந்தையினால் பிரசாதத்தை வாங்காமலும் உண்ணாமலும் அங்கேயே போட்டுவிட்டுத் தன் அரண்மனைக்குச் சென்றான்.

அவன் அரண்மனையை அடைந்ததும் அதிர்ச்சி காத்திருந்தது! அவனது 100 புதல்வர்களும் இறந்து கிடந்தனர், அவனது ராஜ்யமும் செல்வமும் நாசமாகியிருந்தன! அரசன் திகைத்து நின்று, தன் ராஜ அகந்தையினாலும், இடையர்களின் பிரசாதத்தை அவமதித்ததாலும்தான் இந்த கதி நேர்ந்தது என்பதை உணர்ந்தான்.

அரசன் உடனடியாக ஓடோடிச் சென்று காட்டில் அந்த இடையர்களைச் சந்தித்து, தன் அகந்தைக்கு மன்னிப்புக் கேட்டு, அவர்களுடன் சேர்ந்து ஸத்யநாராயண பூஜையைச் செய்து பிரசாதத்தை உட்கொண்டான். பகவானின் அருளால் அவனது 100 புதல்வர்களும் மீண்டும் உயிர் பெற்றனர்! ராஜ்யமும் செல்வமும் மீண்டும் அவனுக்குக் கிடைத்தன.

முடிவுரை:
எவரொருவர் இந்த உன்னதமான ஸ்ரீ ஸத்ய நாராயண விரதத்தைச் செய்கிறார்களோ, அல்லது பக்தியுடன் இக்கதையைக் கேட்கிறார்களோ, அவர்களுக்கு வறுமை, நோய், பயம் அகலும். இஹலோகத்தில் சந்தான பாக்கியம், தீர்க்காயுள், சகல ஐஸ்வர்யங்களும் பெற்று, முடிவில் ஸ்ரீவைகுண்டப் பதவியை அடைவார்கள் என்பதில் எவ்வித சந்தேகமும் இல்லை."

மங்களம்:
காயேன வாசா மனஸேந்த்ரியைர்வா புத்யாத்மனா வா ப்ரக்ருதேஃ ஸ்வபாவாத் |
கரோமி யத்யத் சகலம் பரஸ்மை நாராயணாயேதி சமர்ப்பயாமி ||

மங்களம் கோவிந்தாய! மங்களம் மதுசூதனாய!
ஸ்ரீ ஸத்ய நாராயண ஸ்வாமி திருவடிகளே சரணம்!`,
      content_en: `Chapter 5: King Tungadhwaja and Final Benediction
Suta Puranika concluded: "O Rishis! Once, a King named Tungadhwaja went hunting in the forest. While returning, he saw humble cowherds gathered under a Banyan tree, performing Sri Sathya Narayana Pooja with deep devotion.

Out of royal pride, the King looked down upon the poor cowherds, refused to bow to the deity, and sat at a distance. When the pooja concluded, the cowherds reverently brought the Sathya Narayana Prasad to the King. But the arrogant King rejected the Prasad, left it on the ground, and rode back to his palace.

When he arrived at his palace, tragedy struck! All 100 of his sons lay dead, his treasury was empty, and his kingdom was in ruins! The King broke down, realizing that his royal pride and disrespect towards the Lord's Prasad caused this devastation.

The King immediately ran back to the forest, fell at the feet of the cowherds, begged Lord Sathya Narayana for forgiveness, performed the pooja with them, and ate the sacred Prasad. By Lord Vishnu's divine grace, all 100 of his sons came back to life! His kingdom, wealth, and glory were restored completely.

Conclusion:
Whoever performs this sacred Sri Sathya Narayana Vratam or listens to this Kathamrutam with devotion will be freed from all sorrow, poverty, and fear. They will be blessed with health, wealth, noble children, and ultimate Vaikuntha Liberation."

Mangalam:
Kayena Vacha Manasendriyairva Buddhyatmana Va Prakriteh Swabhavath |
Karomi Yadyat Sakalam Parasmai Narayanayeti Samarpayami ||

Mangalam Govindhaya! Mangalam Madhusudhanaya!
Sri Sathya Narayana Swamy Thiruvadigale Sharanam!`
    }
  };

  const scrollToTop = useCallback(() => {
    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const goToSection = useCallback((idx) => {
    setCurrentSection(idx);
    setTimeout(scrollToTop, 100);
  }, [scrollToTop]);

  const currentItem = SWF_SECTIONS[currentSection];

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #fef7ed 0%, #fff5f0 40%, #fff 100%)' }}>
      {/* Hero Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #b91c1c 0%, #dc2626 30%, #ea580c 70%, #f97316 100%)',
        padding: '40px 20px 32px',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Decorative Elements */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          background: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(255,255,255,0.08) 0%, transparent 50%)',
          pointerEvents: 'none',
        }} />

        <div style={{ position: 'relative', zIndex: 1, maxWidth: '800px', margin: '0 auto' }}>
          {/* Beautiful Framed Portrait of Sri Sathya Narayana Perumal */}
          <div style={{
            maxWidth: '360px',
            margin: '0 auto 20px',
            borderRadius: '16px',
            padding: '8px',
            background: 'linear-gradient(135deg, #BF953F, #FCF6BA, #B38728, #FBF5B7, #AA771C)',
            boxShadow: '0 12px 35px rgba(0,0,0,0.45), 0 0 25px rgba(212,175,55,0.6)',
          }}>
            <div style={{
              borderRadius: '12px',
              overflow: 'hidden',
              background: '#fff',
              border: '2px solid #591717',
            }}>
              <img
                src={`${process.env.PUBLIC_URL}/images/sathya-narayana.jpg`}
                alt="Sri Sathya Narayana Perumal"
                onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '<span style="font-size:80px;display:flex;align-items:center;justify-content:center;height:300px">🙏</span>'; }}
                style={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '460px',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            </div>
          </div>

          <h1 style={{
            fontSize: 'clamp(24px, 5vw, 36px)',
            fontWeight: '900',
            color: '#FFF8E7',
            marginBottom: '8px',
            textShadow: '0 2px 10px rgba(0,0,0,0.5)',
            lineHeight: 1.3,
            letterSpacing: '0.5px',
          }}>
            {isTamil ? 'ஸ்ரீ ஸத்ய நாராயண பூஜா க்ரமம்' : 'Sri Sathya Narayana Pooja Prayogam'}
          </h1>
          <p style={{
            color: 'rgba(255,255,255,0.95)',
            fontSize: 'clamp(14px, 3vw, 18px)',
            maxWidth: '550px',
            margin: '0 auto 12px',
            lineHeight: 1.5,
            fontWeight: '500',
          }}>
            {isTamil
              ? 'ஸத்ய நாராயண விரத பூஜையின் பூர்ண க்ரமம் மற்றும் வழிமுறைகள்'
              : 'Complete Prayogam for Sri Sathya Narayana Vratha Pooja'}
          </p>

          {/* Section Counter Badge */}
          <div style={{
            display: 'inline-block',
            marginTop: '8px',
            padding: '6px 18px',
            background: 'rgba(255,255,255,0.25)',
            borderRadius: '20px',
            color: '#fff',
            fontSize: '14px',
            fontWeight: '700',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255,255,255,0.3)',
          }}>
            📖 {SWF_SECTIONS.length} {isTamil ? 'பகுதிகள்' : 'Sections'}
          </div>
        </div>
      </div>

      {/* Controls Bar */}
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '16px 20px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '12px',
        alignItems: 'center',
        justifyContent: 'space-between',
      }} ref={sectionRef}>

        {/* View Mode Toggle */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px',
          background: '#FAF6EE',
          padding: '4px',
          borderRadius: '10px',
          border: '1px solid #D4AF37',
        }}>
          <button
            onClick={() => setViewMode('text')}
            style={{
              padding: '8px 14px',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: viewMode === 'text' ? '800' : '500',
              background: viewMode === 'text' ? '#CC6600' : 'transparent',
              color: viewMode === 'text' ? '#fff' : '#1A237E',
              transition: 'all 0.2s',
            }}
          >
            📖 {isTamil ? 'உரை வடிவம்' : 'Text Reader'}
          </button>
          <button
            onClick={() => setViewMode('single')}
            style={{
              padding: '8px 14px',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: viewMode === 'single' ? '800' : '500',
              background: viewMode === 'single' ? '#CC6600' : 'transparent',
              color: viewMode === 'single' ? '#fff' : '#1A237E',
              transition: 'all 0.2s',
            }}
          >
            📽️ {isTamil ? 'அனிமேஷன்' : 'Animation SWF'}
          </button>
          <button
            onClick={() => setViewMode('all')}
            style={{
              padding: '8px 14px',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: viewMode === 'all' ? '800' : '500',
              background: viewMode === 'all' ? '#CC6600' : 'transparent',
              color: viewMode === 'all' ? '#fff' : '#1A237E',
              transition: 'all 0.2s',
            }}
          >
            📑 {isTamil ? 'அனைத்தும்' : 'Show All'}
          </button>
        </div>

        {/* Font Size Adjuster for Text Mode */}
        {viewMode === 'text' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#FAF6EE', padding: '4px 10px', borderRadius: '8px', border: '1px solid #D4AF37' }}>
            <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#1A237E' }}>{isTamil ? 'எழுத்து அளவு:' : 'Font Size:'}</span>
            <button onClick={() => setFontSize(Math.max(14, fontSize - 2))} style={{ padding: '2px 8px', borderRadius: '4px', background: '#fff', border: '1px solid #ccc', cursor: 'pointer', fontWeight: 'bold' }}>A-</button>
            <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#CC6600' }}>{fontSize}px</span>
            <button onClick={() => setFontSize(Math.min(24, fontSize + 2))} style={{ padding: '2px 8px', borderRadius: '4px', background: '#fff', border: '1px solid #ccc', cursor: 'pointer', fontWeight: 'bold' }}>A+</button>
          </div>
        )}
      </div>

      {/* Content Area */}
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 20px 40px' }}>
        {viewMode === 'text' ? (
          /* Clean Text Reader View */
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {Object.keys(TEXT_CONTENT).map((secKey) => {
              const sec = TEXT_CONTENT[secKey];
              const secNum = Number(secKey) + 1;
              return (
                <div
                  key={secKey}
                  style={{
                    background: '#FAF6EE',
                    borderRadius: '14px',
                    border: '2px solid #D4AF37',
                    padding: '20px 24px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '14px',
                    paddingBottom: '10px',
                    borderBottom: '1px solid rgba(212, 175, 55, 0.4)',
                  }}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: '#CC6600',
                      color: '#fff',
                      fontSize: '14px',
                      fontWeight: '800',
                      flexShrink: 0,
                    }}>
                      {secNum}
                    </span>
                    <h3 style={{
                      fontSize: '19px',
                      fontWeight: '800',
                      color: '#1A237E',
                      margin: 0,
                    }}>
                      {isTamil ? sec.title_ta : sec.title_en}
                    </h3>
                  </div>

                  <div style={{
                    fontSize: `${fontSize}px`,
                    lineHeight: '1.7',
                    color: '#2d2727',
                    whiteSpace: 'pre-line',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                  }}>
                    {isTamil ? sec.content_ta : sec.content_en}
                  </div>
                </div>
              );
            })}
          </div>
        ) : viewMode === 'single' ? (
          /* Single Section View */
          <div>
            {/* Section Card */}
            <div style={{
              background: '#fff',
              borderRadius: '16px',
              boxShadow: '0 4px 24px rgba(0,0,0,0.06), 0 1px 4px rgba(0,0,0,0.04)',
              overflow: 'hidden',
              border: '1px solid rgba(234, 88, 12, 0.1)',
            }}>
              {/* Section Header */}
              <div style={{
                padding: '16px 24px',
                background: 'linear-gradient(135deg, #fef3c7 0%, #fff7ed 100%)',
                borderBottom: '1px solid rgba(234, 88, 12, 0.1)',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #ea580c, #dc2626)',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: '700',
                  flexShrink: 0,
                }}>
                  {currentSection + 1}
                </span>
                <h2 style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#292524',
                  margin: 0,
                }}>
                  {isTamil ? currentItem.title_ta : currentItem.title_en}
                </h2>
              </div>

              {/* SWF Player */}
              <div style={{ padding: '24px', background: '#fafaf9' }}>
                <SwfPlayer
                  key={currentItem.file}
                  swfFile={currentItem.file}
                  width={550}
                  height={400}
                />
              </div>
            </div>

            {/* Navigation Buttons */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '24px',
              gap: '12px',
            }}>
              <button
                onClick={() => goToSection(currentSection - 1)}
                disabled={currentSection === 0}
                style={{
                  padding: '10px 20px',
                  borderRadius: '10px',
                  border: '1px solid #e7e0d8',
                  background: currentSection === 0 ? '#f5f0eb' : '#fff',
                  color: currentSection === 0 ? '#a8a29e' : '#44403c',
                  cursor: currentSection === 0 ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                ← {isTamil ? 'முந்தைய' : 'Previous'}
              </button>

              <span style={{
                fontSize: '13px',
                color: '#78716c',
                fontWeight: '500',
              }}>
                {currentSection + 1} / {SWF_SECTIONS.length}
              </span>

              <button
                onClick={() => goToSection(currentSection + 1)}
                disabled={currentSection === SWF_SECTIONS.length - 1}
                style={{
                  padding: '10px 20px',
                  borderRadius: '10px',
                  border: 'none',
                  background: currentSection === SWF_SECTIONS.length - 1
                    ? '#f5f0eb'
                    : 'linear-gradient(135deg, #ea580c, #dc2626)',
                  color: currentSection === SWF_SECTIONS.length - 1 ? '#a8a29e' : '#fff',
                  cursor: currentSection === SWF_SECTIONS.length - 1 ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                {isTamil ? 'அடுத்தது' : 'Next'} →
              </button>
            </div>

            {/* Section Quick Navigation Pills */}
            <div style={{
              marginTop: '32px',
              padding: '20px',
              background: '#fff',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              border: '1px solid #f0ebe6',
            }}>
              <h3 style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#78716c',
                marginBottom: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}>
                {isTamil ? 'பகுதிகள்' : 'All Sections'}
              </h3>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '6px',
              }}>
                {SWF_SECTIONS.map((sec, idx) => (
                  <button
                    key={idx}
                    onClick={() => goToSection(idx)}
                    title={isTamil ? sec.title_ta : sec.title_en}
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '8px',
                      border: idx === currentSection ? '2px solid #ea580c' : '1px solid #e7e0d8',
                      background: idx === currentSection
                        ? 'linear-gradient(135deg, #ea580c, #dc2626)'
                        : idx < currentSection ? '#fef3c7' : '#fff',
                      color: idx === currentSection ? '#fff' : '#44403c',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: idx === currentSection ? '700' : '500',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {idx + 1}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          /* Show All View */
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {SWF_SECTIONS.map((sec, idx) => (
              <div
                key={idx}
                id={`section-${idx}`}
                style={{
                  background: '#fff',
                  borderRadius: '16px',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.06), 0 1px 4px rgba(0,0,0,0.04)',
                  overflow: 'hidden',
                  border: '1px solid rgba(234, 88, 12, 0.1)',
                }}
              >
                {/* Section Header */}
                <div style={{
                  padding: '14px 20px',
                  background: 'linear-gradient(135deg, #fef3c7 0%, #fff7ed 100%)',
                  borderBottom: '1px solid rgba(234, 88, 12, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}>
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #ea580c, #dc2626)',
                    color: '#fff',
                    fontSize: '12px',
                    fontWeight: '700',
                    flexShrink: 0,
                  }}>
                    {idx + 1}
                  </span>
                  <h2 style={{
                    fontSize: '16px',
                    fontWeight: '700',
                    color: '#292524',
                    margin: 0,
                  }}>
                    {isTamil ? sec.title_ta : sec.title_en}
                  </h2>
                </div>

                {/* SWF Player */}
                <div style={{ padding: '20px', background: '#fafaf9' }}>
                  <SwfPlayer
                    swfFile={sec.file}
                    width={550}
                    height={400}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer Info */}
      <div style={{
        textAlign: 'center',
        padding: '24px 20px 40px',
        borderTop: '1px solid #f0ebe6',
      }}>
        <p style={{ color: '#a8a29e', fontSize: '12px', lineHeight: 1.6 }}>
          {isTamil
            ? 'இந்த ப்ரயோகம் ahobilam.com க்காக தயாரிக்கப்பட்டது. அனைத்து உரிமைகளும் பாதுகாக்கப்பட்டவை.'
            : 'This prayogam has been prepared for ahobilam.com. All rights reserved.'}
          <br />
          {isTamil
            ? 'Flash உள்ளடக்கம் Ruffle மூலம் இயக்கப்படுகிறது.'
            : 'Flash content powered by Ruffle WebAssembly emulator.'}
        </p>
      </div>
    </div>
  );
}
